import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { PageHead, TableSkeleton, Money } from "./ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["12m", "12 months"], ["all", "All time"]];

export default function AdminRevenue() {
  const [summary, setSummary] = useState(null);
  const [escrow, setEscrow] = useState(null);
  const [range, setRange] = useState("12m");
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/admin/revenue/summary").then((r) => setSummary(r.data));
    api.get("/admin/revenue/escrow").then((r) => setEscrow(r.data));
  }, []);
  useEffect(() => {
    api.get(`/admin/revenue/trend?range=${range}`).then((r) =>
      setTrend(r.data.labels.map((l, i) => ({ label: l, revenue: r.data.values[i] }))));
  }, [range]);

  return (
    <div data-testid="admin-revenue">
      <PageHead title="Revenue" subtitle="Revenue analytics & breakdown across all streams" />

      {escrow && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[["In Escrow", escrow.in_escrow], ["Pending Release", escrow.pending_release],
            ["Released (Month)", escrow.released_this_month], ["Refunded (Month)", escrow.refunded_this_month]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-white p-4 shadow-sm border border-black/5" data-testid={`escrow-${l}`}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{l}</div>
              <div className="mt-1 font-heading text-xl font-extrabold text-[#111827]"><Money value={v} /></div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5 mb-6" data-testid="revenue-trend">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-[#111827]">Revenue Trend</h2>
          <div className="flex gap-1 flex-wrap">
            {RANGES.map(([v, l]) => (
              <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${range === v ? "bg-[#0D5C75] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={trend}>
            <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2A9D8F" stopOpacity={0.4} /><stop offset="100%" stopColor="#2A9D8F" stopOpacity={0} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
            <XAxis dataKey="label" fontSize={11} tickLine={false} />
            <YAxis fontSize={11} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
            <Area type="monotone" dataKey="revenue" stroke="#2A9D8F" strokeWidth={2.5} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5" data-testid="revenue-streams">
        <h2 className="font-heading font-bold text-[#111827] mb-4">Revenue by Stream</h2>
        {!summary ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Stream</TableHead><TableHead className="text-right">This Month</TableHead>
                <TableHead className="text-right">All Time</TableHead><TableHead className="text-right">% of Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {summary.streams.map((s) => (
                  <TableRow key={s.key} data-testid={`stream-${s.key}`}>
                    <TableCell className="font-medium">{s.label}</TableCell>
                    <TableCell className="text-right"><Money value={s.this_month} /></TableCell>
                    <TableCell className="text-right"><Money value={s.all_time} /></TableCell>
                    <TableCell className="text-right">{s.pct_of_total}%</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-bold bg-gray-50">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right"><Money value={summary.total_month} /></TableCell>
                  <TableCell className="text-right"><Money value={summary.total_all_time} /></TableCell>
                  <TableCell className="text-right">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
